'use client';

import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactNode, type Ref } from 'react';

import { uiSfx } from './boardingSfx';
import s from './boarding.module.css';
import gate from './gatedAction.module.css';
import { ARM_PLACARD, ASSETS, DEFAULT_DIRECTION, DOOR, MOTION, TILE_THEMES, type ArmState, type Direction, type TileThemeId, type TitleTakeId, type TypeTakeId } from './copy';
import { prefersReducedMotion } from './prefs';
import { useGatedActionMotion } from './useGatedActionMotion';
import { keyboardMayBeOpen } from './useStableFrameHeight';
import { AmtilSymbol } from '@/components/chrome/AmtilSymbol';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const FIT_TIERS = ['natural', 'compact'] as const;
/** Width the door content is drawn for; a bigger fuselage door scales it up (never down). */
const BASE_CONTENT_W = 380;
const MAX_SCALE = 1.6;
/** Phone floor (director 2026-09-29 "no scrolling"): below this the scene stacks instead. */
const MIN_SCALE = 0.7;
/** Share of the leaf height the scaled content may fill. */
const FILL = 0.9;

/** HangarScene listens: the fuselage leaf cannot hold the form even compact → stack the scene (§4). */
export const DoorOverflowContext = createContext<(() => void) | null>(null);

/** The door's lettering: four takes until the director picks one (prototype picker). Exterior face only. */
export const TypeTakeContext = createContext<TypeTakeId>('B');
export const TitleTakeContext = createContext<TitleTakeId>('legend');
/** Forces an OAuth tile theme (prototype picker); null = draw one at random per visit. */
export const TileThemeContext = createContext<TileThemeId | null>(null);

/** Titles are one line always (director 2026-09-30): a title wider than its line steps its size down to fit. */
function fitTitles(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>('.' + s.title).forEach((el) => {
    el.style.fontSize = '';
    const over = el.scrollWidth - el.clientWidth;
    if (over <= 1 || !el.clientWidth) return;
    const size = parseFloat(getComputedStyle(el).fontSize);
    el.style.fontSize = `${Math.max(12, Math.floor(((size * el.clientWidth) / el.scrollWidth) * 2) / 2)}px`;
  });
}

export function keyboardContentGeometry(visualTop: number, visualHeight: number, leafTop: number) {
  const top = Math.max(16, visualTop - leafTop + 16);
  return { top, maxHeight: Math.max(120, visualTop + visualHeight - (leafTop + top) - 16) };
}

/**
 * The passenger door: riveted chrome rim, the real AMTIL mark on the identity label (no window —
 * director 2026-09-29, addendum §13.3), the leaf that holds the form, and the handle-button. The door IS the form — nothing floats in front of the aircraft.
 * It opens only when the parent says so (after a server success), never on a client guess.
 */
interface DoorProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  direction?: Direction;
  /** Server confirmed → the handle pops out and spins a full turn, then the leaf pops and slides left. */
  open?: boolean;
  /** Fired once the leaf has cleared the opening (immediately under reduced motion). */
  onOpened?: () => void;
  /** Blocks input while the aircraft is still taxiing in. */
  inert?: boolean;
  /** The aircraft is still taxiing: every entrance inside the door holds at its first frame until the park. */
  arriving?: boolean;
  identityStrong?: ReactNode;
  /** Optional training sample reclaims the identity plate space; other entry screens keep it. */
  hideIdentity?: boolean;
  /** Legacy sample marker; exterior screens now always keep full scale and scroll when needed. */
  preserveScale?: boolean;
  /** 'interior' = the cabin side of the door: lining, control handle, slide bustle; no logo plate. */
  face?: 'exterior' | 'interior';
  /** Interior only: the slide arming lever. Choosing a plan arms it; an ended plan shows it deployed. */
  arm?: ArmState;
  /** Exterior identity-plate treatment (director 2026-10-06 review takes). */
  plateTake?: PlateTakeId;
  children: ReactNode;
}

// Director 2026-10-06 evening: the plate takes too much door; smaller plate or a riveted banner with a
// 20% smaller lockup, and a better home for Back. The first five are that round; the rest are the morning takes.
export const PLATE_TAKES = ['compact', 'banner', 'banner-slim', 'stamped', 'back-bottom', 'data', 'engraved', 'enamel', 'stencil'] as const;
export type PlateTakeId = (typeof PLATE_TAKES)[number];
export const isPlateTake = (x: string | null): x is PlateTakeId => (PLATE_TAKES as readonly string[]).includes(x ?? '');

export default function Door({ direction = DEFAULT_DIRECTION, open = false, onOpened, inert = false, arriving = false, identityStrong, hideIdentity = false, preserveScale = false, face = 'exterior', arm = 'disarmed', plateTake = 'data', className, children, ...rest }: DoorProps) {
  const opened = useRef(onOpened);
  opened.current = onOpened;
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => opened.current?.(), prefersReducedMotion() ? 0 : MOTION.doorOpenMs);
    return () => clearTimeout(t);
  }, [open]);

  // Addendum §4: in the fuselage the leaf has a fixed height and the form must fit it with no
  // internal scroll. Step down only as far as needed: `compact` tightens spacing and type (never a
  // control below its §6 target). Still too tall → tell the scene, which stacks (page scroll).
  // A leaf with auto height (stacked layout) always measures `natural`.
  const typeTake = useContext(TypeTakeContext);
  const titleTake = useContext(TitleTakeContext);
  // Drawn after mount so server and client markup agree; the tile grid stays hidden until then (CSS).
  const forcedTiles = useContext(TileThemeContext);
  const [drawnTiles, setDrawnTiles] = useState<TileThemeId | null>(null);
  useEffect(() => setDrawnTiles(TILE_THEMES[Math.floor(Math.random() * TILE_THEMES.length)].id), []);
  const tiles = forcedTiles ?? drawnTiles;
  const leafRef = useRef<HTMLDivElement>(null);
  const toplineRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousHeading = useRef<string | null | undefined>(undefined);
  useIsoLayoutEffect(() => {
    const leaf = leafRef.current;
    const content = contentRef.current;
    if (!leaf || !content || typeof ResizeObserver === 'undefined') return;
    // The fixed identity band sits outside the fitted content (director 2026-10-06): the screen
    // fits the space UNDER the plate, so the plate never moves or scales between pages.
    const availH = () => leaf.clientHeight - (toplineRef.current?.offsetHeight ?? 0);
    const fit = () => {
      if (leaf.dataset.keyboard === 'true') return;
      // An open dropdown is a transient overlay, not content: refitting around it would flip the door
      // into (and back out of) the scroll fit and jump the screen (Opus r5). Its closing refits.
      if (content.querySelector('[aria-haspopup="listbox"][aria-expanded="true"]')) return;
      content.style.zoom = '';
      // Plate doors keep ONE proportion on every screen (director 2026-10-06: "the proportion of the
      // contents changes from the previous screen"): no compact tier, no zoom. A screen taller than the
      // door scrolls inside it at full size instead, and the door never leaves the fuselage.
      if (face === 'exterior') {
        // Measuring natural height temporarily removes the scroll container. Keep the user's
        // place through avatar/annotation updates, but start a new screen at its heading.
        const heading = content.querySelector('h1, h2')?.textContent;
        const sameScreen = heading === previousHeading.current;
        const scrollTop = sameScreen ? content.scrollTop : 0;
        // Choice screens (director 2026-10-09): header and handle stay put, only the options list scrolls.
        const region = content.querySelector<HTMLElement>('[data-door-scroll-region]');
        const regionTop = sameScreen && region ? region.scrollTop : 0;
        previousHeading.current = heading;
        const overflows = () => Math.max(content.offsetHeight, content.scrollHeight) > availH() + 1;
        leaf.dataset.fit = 'natural';
        fitTitles(content);
        if (region && overflows()) {
          leaf.dataset.fit = 'pinned';
          fitTitles(content);
          // A door too short to show at least one full row of options (or a collapsed region) scrolls whole instead.
          // Only write the region's scroll when it changed, so a refit never interrupts the pager's own scrolling.
          if (region.clientHeight >= 88) { if (region.scrollTop !== regionTop) region.scrollTop = regionTop; return; }
          leaf.dataset.fit = 'natural';
        }
        if (overflows()) {
          leaf.dataset.fit = 'scroll';
          fitTitles(content); // the scroll fit sits inside the inner perimeter, so titles have less room
        }
        content.scrollTop = scrollTop;
        return;
      }
      for (const tier of FIT_TIERS) {
        leaf.dataset.fit = tier;
        fitTitles(content);
        if (content.offsetHeight <= availH() + 1) {
          let scale = Math.min(MAX_SCALE, leaf.clientWidth / BASE_CONTENT_W, (FILL * availH()) / content.offsetHeight);
          // Zoom reflows the content at leafW/scale, so it wraps more and grows: re-measure and back off.
          // The content always spans the leaf, so its on-screen width is w0 at any zoom. Scaling the rect by
          // w0 / rect.width gives the on-screen height in every engine (WebKit reports the unzoomed rect).
          const w0 = content.offsetWidth; // leaf-local px, immune to ancestor zoom/transform
          while (scale > 1.02) {
            content.style.zoom = scale.toFixed(3);
            fitTitles(content); // zoom narrows the content's own width
            const r = content.getBoundingClientRect();
            if (r.height * (w0 / r.width) <= FILL * availH()) return;
            scale -= 0.05;
          }
          content.style.zoom = '';
          fitTitles(content);
          return;
        }
      }
      // Still too tall at compact: shrink the whole form (a phone door is narrow and short).
      leaf.dataset.fit = 'compact';
      fitTitles(content);
      const w0 = content.offsetWidth;
      for (let scale = Math.min(1, (FILL * availH()) / content.offsetHeight); scale >= MIN_SCALE; scale -= 0.04) {
        content.style.zoom = scale.toFixed(3);
        const r = content.getBoundingClientRect();
        if (r.height * (w0 / r.width) <= availH()) return;
      }
      // Still too tall at the floor: hold the smallest scale and let the door itself scroll, so every
      // control stays reachable while the door never leaves the fuselage (director 2026-10-05).
      content.style.zoom = String(MIN_SCALE);
      leaf.dataset.fit = 'overflow';
    };
    fit();
    // Deferred a frame: a tier change resizes `content`, which must not re-enter this observer's pass.
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    });
    ro.observe(leaf);
    ro.observe(content);
    // A new screen can swap the title without changing the content's size; web fonts land late.
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    });
    mo.observe(content, { childList: true, subtree: true, characterData: true });
    let disposed = false;
    document.fonts?.ready.then(() => {
      if (disposed) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    });
    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
  }, [typeTake, titleTake, preserveScale, hideIdentity, face]);

  // When iOS reduces the visual viewport for the keyboard, the aircraft frame intentionally stays
  // put. Move the focused control inside the keyboard-sized content scroller instead of asking the
  // browser to scroll the fixed shell (which leaves the action handle below the keyboard).
  useEffect(() => {
    const content = contentRef.current;
    const leaf = leafRef.current;
    const shell = leaf?.parentElement;
    const visualViewport = window.visualViewport;
    if (!content || !leaf || !shell || !visualViewport) return;
    // iOS changes innerHeight when the keyboard appears. Capture the layout
    // height once, then refresh it only when the layout width changes (rotation
    // or a real breakpoint change). A same-width keyboard resize must compare
    // against the pre-keyboard portrait/landscape baseline.
    let baseline = { width: window.innerWidth, height: window.innerHeight };
    let raf = 0;
    const refreshBaseline = () => {
      if (Math.abs(window.innerWidth - baseline.width) <= 48) return;
      baseline = { width: window.innerWidth, height: window.innerHeight };
    };
    const sync = () => {
      refreshBaseline();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const keyboardOpen = keyboardMayBeOpen() && visualViewport.height < baseline.height - 120 && content.contains(document.activeElement);
        if (!keyboardOpen) {
          shell.dataset.keyboard = 'false';
          content.style.position = '';
          content.style.top = '';
          content.style.maxHeight = '';
          content.style.overflowY = '';
          return;
        }
        const leafRect = leaf.getBoundingClientRect();
        const geometry = keyboardContentGeometry(visualViewport.offsetTop, visualViewport.height, leafRect.top);
        shell.dataset.keyboard = 'true';
        content.style.position = 'absolute';
        content.style.top = `${geometry.top}px`;
        content.style.maxHeight = `${geometry.maxHeight}px`;
        content.style.overflowY = 'auto';
        const focused = document.activeElement;
        if (!(focused instanceof HTMLElement) || !content.contains(focused)) return;
        const viewport = content.getBoundingClientRect();
        const focusedRect = focused.getBoundingClientRect();
        const top = viewport.top + 16;
        const bottom = viewport.bottom - 16;
        if (focusedRect.top < top) content.scrollTop -= top - focusedRect.top;
        else if (focusedRect.bottom > bottom) content.scrollTop += focusedRect.bottom - bottom;
      });
    };
    const onFocusIn = () => sync();
    content.addEventListener('focusin', onFocusIn);
    window.addEventListener('resize', sync);
    visualViewport.addEventListener('resize', sync);
    visualViewport.addEventListener('scroll', sync);
    return () => {
      cancelAnimationFrame(raf);
      content.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('resize', sync);
      visualViewport.removeEventListener('resize', sync);
      visualViewport.removeEventListener('scroll', sync);
    };
  }, []);

  return (
    <div {...rest} className={[s.door, className].filter(Boolean).join(' ')} data-door-shell data-arriving={arriving ? 'true' : undefined} data-fit-policy={preserveScale ? 'preserve-scale' : 'adaptive'} data-direction={direction} data-face={face} data-arm={face === 'interior' ? arm : undefined} data-type-take={face === 'exterior' ? typeTake : undefined} data-title-take={face === 'exterior' ? titleTake : undefined} data-tiles={tiles ?? undefined} data-open={open ? 'true' : undefined}>
      <div className={s.cabin} aria-hidden="true" />
      {face === 'exterior' && (
        /* Two guide arms (director 2026-10-21): the open leaf hangs on them, never free-floating.
           They swing out on the leaf's own clock during the push-out, hold deployed while the
           leaf translates along them, and the leaf paints above them — so each arm disappears
           behind the panel as if bolted to its inner face. */
        <div className={s.swingArms} data-door-arms aria-hidden="true">
          {[s.swingArmTop, s.swingArmLow].map((pos) => (
            <span key={pos} className={[s.swingArm, pos].join(' ')}><i className={s.armStrut}><b className={s.armKnuckle} /></i></span>
          ))}
        </div>
      )}
      <div ref={leafRef} className={s.leaf} data-door-leaf>
        {face === 'interior' ? (
          <>
            {/* The cabin side (director 2026-09-29): recessed control handle up top, slide bustle below. */}
            <div className={s.ctrlHandle} aria-hidden="true">
              <span className={s.ctrlLever} />
              <span className={s.ctrlPlacard}>LIFT TO OPEN</span>
            </div>
            <div className={s.bustle} aria-hidden="true">
              {arm === 'deployed' && <span className={s.slide} />}
              <span className={s.armLever} />
              <span className={s.armPlacard}>{ARM_PLACARD[arm]}</span>
            </div>
          </>
        ) : (
          <>
            <div className={s.rivets} aria-hidden="true">
              <i /><i /><i /><i /><i /><i />
            </div>
            {/* Once the form clears off the opening leaf, the bare skin carries the AMTIL symbol (director 2026-10-10). */}
            <span className={s.skinMark} data-door-skin-mark aria-hidden="true"><AmtilSymbol height="100%" /></span>
          </>
        )}
        {/* The door's top: the real AMTIL lockup on a riveted data plate (director pick B, 2026-09-29).
            On the exterior it lives OUTSIDE the fitted content, so it never moves or scales with the
            screen below it — one fixed location on every page (director 2026-10-06). */}
        {face === 'exterior' && !hideIdentity && (
          <div ref={toplineRef} className={s.topline} data-door-identity data-plate-take={plateTake}>
            <span className={s.plate} data-door-identity-plate>
              <img className={s.doorLogo} src={plateTake === 'enamel' ? ASSETS.lockupWhite : ASSETS.lockupBlack} alt="AMTIL" />
            </span>
            {identityStrong && <strong className={s.identity}>{identityStrong}</strong>}
          </div>
        )}
        <div ref={contentRef} className={s.content} data-door-content inert={inert || open}>
          {open && <p className={s.srOnly} role="status">{DOOR.opening}</p>}
          {face === 'interior' && !hideIdentity && identityStrong && <div className={s.topline} data-door-identity>
            <strong className={s.identity}>{identityStrong}</strong>
          </div>}
          {children}
        </div>
      </div>
    </div>
  );
}

/** The door frame reused as an interior (Access reason, found note chrome). */
export function DoorInterior({ direction = DEFAULT_DIRECTION, eyebrow, title, arm, back, titleRef, children, ...rest }: { direction?: Direction; eyebrow: ReactNode; title: ReactNode; arm?: ArmState; /** Back, pinned above the title like every door step. */ back?: ReactNode; titleRef?: Ref<HTMLHeadingElement>; children: ReactNode } & Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children'>) {
  return (
    <Door direction={direction} identityStrong={eyebrow} face="interior" arm={arm} {...rest}>
      {back}
      <h2 className={s.title} ref={titleRef} tabIndex={titleRef ? -1 : undefined}>{title}</h2>
      {children}
    </Door>
  );
}

/**
 * The handle-button: a metallic grip around a labelled blue action (the label says what it does).
 * Pending → "Checking…" and duplicate presses are ignored (aria-disabled, never `disabled`, so it
 * stays focusable and says why). `aside` sits beside the handle (Turnstile's visible state).
 */
export function HandleButton({ label, pending = false, disabled = false, onClick, type = 'submit', aside, turning = false, forceFocus = false }: {
  label: string;
  pending?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'submit' | 'button';
  aside?: ReactNode;
  turning?: boolean;
  forceFocus?: boolean;
}) {
  const helpId = useId();
  const gated = disabled && !pending;
  const instant = useGatedActionMotion();
  return (
    <div className={s.latchWrap}>
      {aside && <div className={s.latchTop}>{aside}</div>}
      <div className={[gate.frame, gated ? gate.closed : gate.open, instant ? gate.instant : ''].filter(Boolean).join(' ')} data-gate-state={gated ? 'blocked' : pending ? 'busy' : 'ready'}>
        {gated && <p id={helpId} className={gate.help}>Complete the required selection to continue.</p>}
        <button
          type={type}
          disabled={gated}
          className={[s.latch, gate.button, turning ? s.latchTurning : '', forceFocus ? s.focusRing : ''].filter(Boolean).join(' ')}
          aria-busy={pending || undefined}
          aria-disabled={pending || disabled || undefined}
          aria-describedby={gated ? helpId : undefined}
          onClick={(e) => {
            if (pending || disabled) {
              e.preventDefault();
              return;
            }
            uiSfx('confirm');
            onClick?.();
          }}
        >
          <span className={[s.latchInner, gate.inner].join(' ')}>
            <span className={s.grip} aria-hidden="true" />
            {pending ? DOOR.handleChecking : label}
          </span>
        </button>
      </div>
    </div>
  );
}
